/* eslint-disable-next-line no-restricted-imports */
import * as t from 'io-ts';

import { tProductKind } from './product';

// ============================================================================
// Constants
// ============================================================================

export const PRODUCT_TYPE = 'product';
export const EQUIVALENCY_TYPE = 'equivalency';

// ============================================================================
// Enums
// ============================================================================

export enum CrmProvider {
  alpineiq = 'alpineiq',
  annex_cloud = 'annex_cloud',
  data_owl = 'data_owl',
  springbig = 'springbig',
}

// ============================================================================
// Validation Error Class
// ============================================================================

export class ValidationError extends Error {
  override name = 'ValidationError';
  public validationErrors: t.ValidationError[];
  public invalidData: unknown;

  constructor(errors: t.ValidationError[], data: unknown) {
    super('Validation Error');
    this.validationErrors = errors;
    this.invalidData = data;
  }
}

// ============================================================================
// Photo / Image Schemas
// ============================================================================

export const tPhoto = t.type({
  id: t.union([t.number, t.string]),
  position: t.number,
  urls: t.type({
    extraLarge: t.string,
    medium: t.string,
    original: t.string,
    small: t.string,
  }),
});

const t_imageResizingUrls = t.type({
  small: t.union([t.string, t.null]),
  medium: t.union([t.string, t.null]),
  extraLarge: t.union([t.string, t.null]),
  original: t.union([t.string, t.null]),
});

export const tPublicImage = t.type({
  id: t.number,
  position: t.union([t.null, t.number]),
  urls: t_imageResizingUrls,
});

// ============================================================================
// Reservation Mode Schemas
// ============================================================================

export const tReservationMode = t.union([
  t.literal('delivery'),
  t.literal('pickup'),
  t.literal('kiosk'),
  t.literal('curbside'),
]);

export const tReservationModeV2 = t.union([
  t.literal('delivery'),
  t.literal('pickup'),
  t.literal('retail'),
  t.literal('curbside'),
]);

export type ReservationModeV2 = t.TypeOf<typeof tReservationModeV2>;

// ============================================================================
// Applicability Rules / Custom Row Schemas
// ============================================================================

export const tApplicabilityRules = t.type({
  filter_type: t.string,
  filter_value: t.string,
});

const optionalAttributes = t.partial({
  enabled_for_menu_product: t.boolean,
  ranking: t.number,
  roots_only: t.boolean,
});

export const tCustomRowSetting = t.intersection([
  t.type({
    applicability_rules: t.array(tApplicabilityRules),
    custom_display_name: t.union([t.string, t.null]),
    enabled: t.boolean,
    id: t.union([t.string, t.number]),
    row_type: t.string,
  }),
  optionalAttributes,
]);

// ============================================================================
// Cart Limit Schemas
// ============================================================================

const tId = t.union([t.string, t.number]);

const tNuProductTypeRequired = t.type({
  id: tId,
  product_type: tProductKind,
  product_subtype: t.union([t.null, t.string]),
});

const tNuProductTypeOptional = t.partial({ _destroy: t.boolean });

export const tNuProductType = t.intersection([
  tNuProductTypeRequired,
  tNuProductTypeOptional,
]);

export type NuProductType = t.TypeOf<typeof tNuProductType>;

export const tRuleProduct = t.intersection([
  tNuProductType,
  t.type({ cart_limit_rule_product_type_id: tId }),
]);

// ============================================================================
// Store Integration Schemas
// ============================================================================

export const tMonerisIntegration = t.type({
  autocapture_on_checkout: t.union([t.boolean, t.undefined]),
  enabled: t.union([t.boolean, t.undefined]),
  id: t.union([t.number, t.undefined]),
  moneris_api_token: t.string,
  moneris_checkout_id: t.string,
  moneris_store_id: t.union([t.string, t.undefined]),
});

export const tCanpayV2Integration = t.type({
  api_secret: t.union([t.string, t.undefined]),
  app_key: t.union([t.string, t.undefined]),
  enabled: t.union([t.boolean, t.undefined]),
  enabled_for_checkout: t.union([t.boolean, t.undefined]),
  id: t.union([t.undefined, t.string, t.number]),
  store_id: t.union([t.string, t.number, t.undefined]),
});

export const tCanPayV2RemotePayIntegration = t.type({
  api_secret: t.union([t.string, t.undefined]),
  app_key: t.union([t.string, t.undefined]),
  enabled: t.union([t.boolean, t.undefined]),
  id: t.union([t.undefined, t.string, t.number]),
});

export const tPayfirmaIntegration = t.type({
  currency: t.string,
  enabled: t.boolean,
  id: t.union([t.string, t.number, t.undefined]),
  payfirma_client_id: t.union([t.string, t.undefined]),
  payfirma_client_secret: t.union([t.string, t.undefined]),
  payfirma_custom_id: t.string,
});

export const tSubdomainSetting = t.intersection([
  t.partial({
    id: t.number,
  }),
  t.type({
    home_url: t.string,
  }),
]);

const tStackingOptions = t.union([
  t.literal('yes'),
  t.literal('no'),
  t.literal('combinable'),
]);

const tRoundingStrategy = t.union([
  t.literal('none'),
  t.literal('nearest'),
  t.literal('up'),
  t.literal('down'),
]);

export const tStoreDiscountSettings = t.type({
  id: t.number,
  loyalty_points_stacking: t.boolean,
  max_specials_per_cart: t.union([t.number, t.null]),
  product_specials_stacking_setting: tStackingOptions,
  bundle_specials_stacking_setting: tStackingOptions,
  bulk_pricing_specials_stacking_setting: tStackingOptions,
  group_specials_stacking_setting: tStackingOptions,
  cart_total_specials_stacking_setting: tStackingOptions,
  rounding_strategy: tRoundingStrategy,
  rounding_increment: t.union([t.number, t.null]),
});
export type StoreDiscountSettings = t.TypeOf<typeof tStoreDiscountSettings>;

export const tTipSetting = t.type({
  custom_amounts: t.array(t.number),
  enabled_for: t.union([
    t.literal('delivery'),
    t.literal('pickup'),
    t.literal('curbside'),
  ]),
  id: t.number,
  variant: t.union([t.literal('dollar'), t.literal('percentage')]),
});

export const tStoreChatSetting = t.type({
  id: t.union([t.number, t.null, t.undefined]),
  enabled: t.union([t.boolean, t.null, t.undefined]),
});
export type StoreChatSetting = t.TypeOf<typeof tStoreChatSetting>;

// ============================================================================
// CRM Integration Schema
// ============================================================================

export const tStoreCrmIntegration = t.type({
  id: t.union([t.number, t.undefined]),
  token: t.union([t.string, t.null]),
  enabled: t.union([t.boolean, t.null]),
  automatic_redemption: t.union([t.boolean, t.null]),
  bypass_redemption: t.union([t.boolean, t.null, t.undefined]),
  crm_provider: t.keyof(CrmProvider),
  max_redemption_count: t.union([t.number, t.undefined, t.null]),
  _destroy: t.union([t.boolean, t.undefined]),
  settings: t.union([t.record(t.string, t.any), t.undefined]),
});

// ============================================================================
// Delivery / Geofence Schemas
// ============================================================================

export const tDeliveryZipcode = t.type({
  fee: t.string,
  id: t.number,
  last_call_minutes: t.number,
  lead_time_minutes: t.number,
  min_amount: t.string,
  zipcode: t.string,
});

export const tDeliveryConfigStrategy = t.type({
  delivery_strategy: t.union([t.string, t.null]),
  id: t.union([t.number, t.null]),
});

export const tFreeDeliveryConfigV2 = t.type({
  id: t.number,
  cart_minimum: t.union([t.null, t.string]),
});

// ============================================================================
// Checkout Agreement Schemas
// ============================================================================

const tConsentRequirement = t.union([
  t.literal('required_once'),
  t.literal('required_always'),
  t.literal('optional_until_consented'),
  t.literal('optional_always'),
]);

export const tCheckoutAgreementV2 = t.type({
  active: t.boolean,
  consent_default: t.union([t.boolean, t.null]),
  consent_requirement: tConsentRequirement,
  external_link: t.union([t.string, t.null]),
  external_links: t.array(t.type({ label: t.string, url: t.string })),
  id: t.number,
  label: t.string,
  name: t.string,
  reservation_mode: t.union([tReservationModeV2, t.null]),
});
export type CheckoutAgreementV2 = t.TypeOf<typeof tCheckoutAgreementV2>;

// ============================================================================
// Status Message Schemas
// ============================================================================

const tCartStatusOptions = t.union([
  t.literal('verification'),
  t.literal('preparing'),
  t.literal('delivery_started'),
  t.literal('ready_for_pickup'),
  t.literal('dismissed'),
]);

export const tStatusMessageV2 = t.type({
  cart_status: tCartStatusOptions,
  enabled: t.boolean,
  message_text: t.union([t.string, t.null]),
  use_default: t.boolean,
  id: t.union([t.string, t.number]),
  kiosk: t.union([t.null, t.boolean]),
});

// ============================================================================
// Medical Documentation Schemas
// ============================================================================

export const tMedicalStoreDocumentationRequirement = t.intersection([
  t.type({
    id: t.union([t.string, t.number]),
    require_medical_id: t.union([
      t.literal('number'),
      t.literal('photo'),
      t.literal('photo_and_number'),
      t.literal('none'),
    ]),
  }),
  t.partial({
    require_medical_expiration_date: t.boolean,
  }),
]);

// ============================================================================
// Curbside Pickup Schemas
// ============================================================================

export const tCurbsidePickupSettingObject = t.type({
  form_enabled: t.boolean,
  id: t.union([t.string, t.number]),
  instructions: t.union([t.string, t.null]),
  setting: t.union([
    t.literal('allowed'),
    t.literal('mandatory'),
    t.literal('not_allowed'),
  ]),
});
